import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const PlayerComparisonContext = createContext(null);

// localStorage keys
const STORAGE_KEYS = {
  COMPARISON_GROUPS: 'brownlow_genius_comparison_groups',
  EXPANDED_MOBILE_ROWS: 'brownlow_genius_expanded_mobile_rows'
};

// Helper functions for localStorage
const loadFromStorage = (key, defaultValue) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.warn(`Failed to load ${key} from localStorage:`, error);
    return defaultValue;
  }
};

const saveToStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Failed to save ${key} to localStorage:`, error);
  }
};

export const usePlayerComparison = () => {
  const ctx = useContext(PlayerComparisonContext);
  if (!ctx) {
    throw new Error('usePlayerComparison must be used within a PlayerComparisonProvider');
  }
  return ctx;
};

export const PlayerComparisonProvider = ({ children }) => {
  // Load initial state from localStorage or use defaults
  const [comparisonGroups, setComparisonGroups] = useState(() => 
    loadFromStorage(STORAGE_KEYS.COMPARISON_GROUPS, [{ id: 1, selectedPlayers: [] }])
  );
  
  const [expandedMobileRows, setExpandedMobileRows] = useState(() =>
    loadFromStorage(STORAGE_KEYS.EXPANDED_MOBILE_ROWS, {})
  );

  // Save comparison groups to localStorage whenever they change
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.COMPARISON_GROUPS, comparisonGroups);
  }, [comparisonGroups]);

  // Save expanded mobile rows to localStorage whenever they change
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.EXPANDED_MOBILE_ROWS, expandedMobileRows);
  }, [expandedMobileRows]);

  // Add a new comparison group
  const addComparisonGroup = useCallback(() => {
    const newGroupId = Math.max(...comparisonGroups.map(g => g.id), 0) + 1;
    setComparisonGroups(prev => [
      ...prev,
      { id: newGroupId, selectedPlayers: [] }
    ]);
  }, [comparisonGroups]);

  // Remove a comparison group
  const removeComparisonGroup = useCallback((groupId) => {
    setComparisonGroups(prev => {
      if (prev.length <= 1) return prev; // Keep at least one group
      return prev.filter(group => group.id !== groupId);
    });
    
    // Clean up expanded mobile rows for this group
    setExpandedMobileRows(prev => {
      const newState = { ...prev };
      delete newState[groupId];
      return newState;
    });
  }, []);

  // Update selected players for a specific group
  const updateGroupPlayers = useCallback((groupId, selectedPlayers) => {
    setComparisonGroups(prev => 
      prev.map(group => 
        group.id === groupId 
          ? { ...group, selectedPlayers }
          : group
      )
    );
  }, []);

  // Add a player to a specific group
  const addPlayerToGroup = useCallback((groupId, playerName) => {
    setComparisonGroups(prev => 
      prev.map(group => 
        group.id === groupId 
          ? { 
              ...group, 
              selectedPlayers: group.selectedPlayers.includes(playerName) 
                ? group.selectedPlayers 
                : [...group.selectedPlayers, playerName]
            }
          : group
      )
    );
  }, []);

  // Remove a player from a specific group
  const removePlayerFromGroup = useCallback((groupId, playerName) => {
    setComparisonGroups(prev => 
      prev.map(group => 
        group.id === groupId 
          ? { 
              ...group, 
              selectedPlayers: group.selectedPlayers.filter(p => p !== playerName)
            }
          : group
      )
    );
  }, []);

  // Clear all players from a specific group
  const clearGroupPlayers = useCallback((groupId) => {
    setComparisonGroups(prev => 
      prev.map(group => 
        group.id === groupId 
          ? { ...group, selectedPlayers: [] }
          : group
      )
    );
  }, []);

  // Get selected players for a specific group
  const getGroupPlayers = useCallback((groupId) => {
    const group = comparisonGroups.find(g => g.id === groupId);
    return group ? group.selectedPlayers : [];
  }, [comparisonGroups]);

  // Mobile row expansion management
  const toggleMobileRow = useCallback((groupId, playerName) => {
    setExpandedMobileRows(prev => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        [playerName]: !prev[groupId]?.[playerName]
      }
    }));
  }, []);

  const isMobileRowExpanded = useCallback((groupId, playerName) => {
    return !!expandedMobileRows[groupId]?.[playerName];
  }, [expandedMobileRows]);

  // Clear all comparison data (useful for logout/reset)
  const clearAllComparisons = useCallback(() => {
    const defaultGroups = [{ id: 1, selectedPlayers: [] }];
    const defaultRows = {};
    
    setComparisonGroups(defaultGroups);
    setExpandedMobileRows(defaultRows);
    
    // Also clear localStorage
    saveToStorage(STORAGE_KEYS.COMPARISON_GROUPS, defaultGroups);
    saveToStorage(STORAGE_KEYS.EXPANDED_MOBILE_ROWS, defaultRows);
  }, []);



  const value = {
    // State
    comparisonGroups,
    expandedMobileRows,
    
    // Group management
    addComparisonGroup,
    removeComparisonGroup,
    clearAllComparisons,
    
    // Player management
    updateGroupPlayers,
    addPlayerToGroup,
    removePlayerFromGroup,
    clearGroupPlayers,
    getGroupPlayers,
    
    // Mobile UI management
    toggleMobileRow,
    isMobileRowExpanded,
  };

  return (
    <PlayerComparisonContext.Provider value={value}>
      {children}
    </PlayerComparisonContext.Provider>
  );
};
